/**
 * Blocked-read telemetry — pure helpers (2026-10-16).
 *
 * Why: the 2026-10-15/16 firestore.rules lockdowns were emulator-tested only.
 * A rule that breaks a real screen shows up client-side as a FirestoreError
 * `permission-denied` that most hooks swallow (empty list, spinner, nothing in
 * the console in prod). This module turns those into anonymous, low-volume
 * records so the hourly ops digest can flag them within hours.
 *
 * ☠️ PII: nothing here may carry a uid, name, email, phone or document id.
 * Paths and routes are normalized to their PATTERN before they leave the
 * browser; labels from console messages are scrubbed the same way.
 *
 * Mirrored in Dart: c1_app lib/core/observability/permission_telemetry.dart —
 * keep the normalizers in step (same fixtures in both test suites).
 */

export type FirestoreOp = 'get' | 'list' | 'listen' | 'write' | 'unknown';
export type RoleBucket = 'worker' | 'staff' | 'anon' | 'unknown';
export type CaptureKind = 'site' | 'global';

export const MAX_PATH_PATTERN = 200;
export const MAX_SCREEN = 120;
export const MAX_LABEL = 80;
export const MAX_VERSION = 40;

/** True for a Firestore permission-denied (not a callable's `functions/permission-denied`). */
export function isPermissionDeniedError(err: unknown): boolean {
  if (!err) return false;
  try {
    if (typeof err === 'string') return /missing or insufficient permissions/i.test(err);
    const e = err as { code?: unknown; message?: unknown; name?: unknown };
    const code = typeof e.code === 'string' ? e.code : '';
    if (code === 'permission-denied' || code === 'firestore/permission-denied') return true;
    if (code && code.includes('/')) return false; // functions/permission-denied, storage/unauthorized …
    const msg = typeof e.message === 'string' ? e.message : '';
    return /missing or insufficient permissions/i.test(msg);
  } catch {
    return false;
  }
}

const AUTO_ID_RE = /^[A-Za-z0-9_-]{16,}$/;
const EMAIL_RE = /[^\s@/]+@[^\s@/]+\.[^\s@/]+/g;
const PHONEISH_RE = /\+?\d[\d\s().-]{6,}\d/g;

/** A route / label token that is (probably) an identifier, not a word. */
export function looksLikeId(seg: string): boolean {
  if (!seg) return false;
  if (seg.includes('@')) return true;
  if (/^\d+$/.test(seg)) return true;
  if (AUTO_ID_RE.test(seg) && /\d/.test(seg)) return true; // Firestore auto ids / uids (20/28 chars)
  if (seg.length >= 6 && /\d/.test(seg) && /^[A-Za-z0-9_-]+$/.test(seg) && /[A-Za-z]/.test(seg) && !/^[a-z]+\d{1,2}$/i.test(seg)) {
    // JO-1234, abc123def — but keep short "c1", "v2", "step3" words.
    return true;
  }
  return false;
}

/** Placeholder used for a document id under the given collection. */
function docPlaceholder(collectionName: string): string {
  if (collectionName === 'tenants') return '{t}';
  if (collectionName === 'users') return '{uid}';
  return '{id}';
}

/**
 * `tenants/BCiP…/assignments/abc` → `tenants/{t}/assignments/{id}`.
 * Collection segments (even index) are kept unless they look like ids;
 * every document segment (odd index) becomes a placeholder.
 * Collection-group queries arrive as `**\/name` and stay as-is.
 */
export function normalizeFirestorePath(raw: string | null | undefined): string {
  if (!raw || typeof raw !== 'string') return 'unknown';
  let path = raw.trim();
  // Full resource names: projects/p/databases/(default)/documents/…
  const marker = '/documents/';
  const at = path.indexOf(marker);
  if (at >= 0) path = path.slice(at + marker.length);
  path = path.replace(/^\/+|\/+$/g, '');
  if (!path) return 'unknown';
  if (path.startsWith('**/')) {
    const group = path.slice(3).split('/')[0] || 'unknown';
    return `**/${looksLikeId(group) ? '{c}' : group}`.slice(0, MAX_PATH_PATTERN);
  }
  const segs = path.split('/');
  const out: string[] = [];
  for (let i = 0; i < segs.length; i += 1) {
    const seg = segs[i];
    if (i % 2 === 0) out.push(looksLikeId(seg) || seg.length > 60 ? '{c}' : seg);
    else out.push(docPlaceholder(segs[i - 1]));
  }
  return out.join('/').slice(0, MAX_PATH_PATTERN);
}

/** `/users/abc123…/timesheets?x=1` → `/users/:id/timesheets`. */
export function normalizeRoute(pathname: string | null | undefined): string {
  if (!pathname || typeof pathname !== 'string') return 'unknown';
  const bare = pathname.split(/[?#]/)[0] || '/';
  const segs = bare.split('/').map((s) => {
    if (!s) return s;
    let decoded = s;
    try {
      decoded = decodeURIComponent(s);
    } catch {
      /* keep raw */
    }
    return looksLikeId(decoded) || decoded.length > 40 ? ':id' : decoded;
  });
  const joined = segs.join('/') || '/';
  return joined.slice(0, MAX_SCREEN);
}

/**
 * Best-effort raw path from a DocumentReference / CollectionReference / Query.
 * Queries expose `_query.path` + `_query.collectionGroup` (SDK internals —
 * fail-soft to 'unknown' if a future SDK renames them).
 */
export function rawPathFromTarget(target: unknown): string | null {
  if (!target || typeof target !== 'object') return null;
  try {
    const t = target as Record<string, any>;
    if (typeof t.path === 'string' && (t.type === 'document' || t.type === 'collection')) return t.path;
    const q = t._query;
    if (q) {
      if (typeof q.collectionGroup === 'string' && q.collectionGroup) return `**/${q.collectionGroup}`;
      const p = q.path;
      if (p) {
        if (typeof p.canonicalString === 'function') return String(p.canonicalString());
        if (Array.isArray(p.segments)) {
          const off = Number(p.offset) || 0;
          const len = typeof p.len === 'number' ? p.len : p.segments.length - off;
          return p.segments.slice(off, off + len).join('/');
        }
      }
    }
    if (typeof t.path === 'string') return t.path;
  } catch {
    /* fall through */
  }
  return null;
}

/**
 * Scrub a free-text console label (e.g. "Error fetching interviews count:")
 * down to something safe to store: no emails / phone numbers / ids.
 */
export function sanitizeLabel(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  let s = raw.replace(EMAIL_RE, ':email').replace(PHONEISH_RE, ':n');
  s = s
    .split(/(\s+|[/:,()[\]{}"'=])/)
    .map((tok) => (tok && /[A-Za-z0-9]/.test(tok) && looksLikeId(tok) ? ':id' : tok))
    .join('');
  s = s.replace(/\s+/g, ' ').trim();
  if (!s) return null;
  return s.slice(0, MAX_LABEL);
}

/**
 * Per-key fixed window limiter (max N per key per window) + a hard
 * per-session cap. Pure: inject the clock in tests.
 */
export interface RateLimiter {
  allow(key: string): boolean;
  /** Total admitted so far (all keys). */
  readonly admitted: number;
}

export function createRateLimiter(opts: {
  windowMs: number;
  maxPerKey?: number;
  maxTotal?: number;
  now?: () => number;
}): RateLimiter {
  const maxPerKey = opts.maxPerKey ?? 1;
  const maxTotal = opts.maxTotal ?? Number.POSITIVE_INFINITY;
  const now = opts.now ?? (() => Date.now());
  const hits = new Map<string, { start: number; n: number }>();
  let admitted = 0;
  return {
    allow(key: string): boolean {
      if (admitted >= maxTotal) return false;
      const t = now();
      const h = hits.get(key);
      if (!h || t - h.start >= opts.windowMs) {
        hits.set(key, { start: t, n: 1 });
        admitted += 1;
        return true;
      }
      if (h.n >= maxPerKey) return false;
      h.n += 1;
      admitted += 1;
      return true;
    },
    get admitted() {
      return admitted;
    },
  };
}

export interface PermissionErrorRecord {
  pathPattern: string;
  op: FirestoreOp;
  role: RoleBucket;
  screen: string;
  capture: CaptureKind;
  source: 'web' | 'app';
  platform: string;
  appVersion: string;
  label: string | null;
}

export function buildRecord(input: {
  rawPath?: string | null;
  op?: FirestoreOp;
  role?: RoleBucket;
  route?: string | null;
  capture: CaptureKind;
  platform?: string;
  appVersion?: string;
  label?: unknown;
  source?: 'web' | 'app';
}): PermissionErrorRecord {
  return {
    pathPattern: normalizeFirestorePath(input.rawPath ?? null),
    op: input.op ?? 'unknown',
    role: input.role ?? 'unknown',
    screen: normalizeRoute(input.route ?? null),
    capture: input.capture,
    source: input.source ?? 'web',
    platform: String(input.platform ?? 'web').slice(0, 20),
    appVersion: String(input.appVersion ?? 'unknown').slice(0, MAX_VERSION),
    label: sanitizeLabel(input.label),
  };
}

/** Limiter key: one record per path × op × screen × capture. */
export function recordKey(r: PermissionErrorRecord): string {
  return `${r.capture}|${r.pathPattern}|${r.op}|${r.screen}`;
}

/**
 * onSnapshot(target, …rest) argument shapes → the same args with the error
 * handler wrapped so `report(err)` runs first. Handles:
 *   (onNext, onError?, onCompletion?)
 *   (observer)
 *   (options, onNext, onError?, onCompletion?)
 *   (options, observer)
 * When the caller passed no error handler we add one (report only) — the SDK
 * would otherwise just log it, and logging is silenced in prod.
 */
export function wrapSnapshotArgs(rest: unknown[], report: (err: unknown) => void): unknown[] {
  const args = [...rest];
  const isObserver = (v: unknown) =>
    !!v && typeof v === 'object' && ['next', 'error', 'complete'].some((k) => typeof (v as any)[k] === 'function');
  let i = 0;
  if (args.length && args[0] && typeof args[0] === 'object' && !isObserver(args[0])) i = 1; // SnapshotListenOptions
  const first = args[i];
  if (isObserver(first)) {
    const obs = first as { next?: Function; error?: Function; complete?: Function };
    const origError = obs.error;
    args[i] = {
      next: obs.next ? (...a: unknown[]) => (obs.next as Function).apply(obs, a) : undefined,
      error: (err: unknown) => {
        safeCall(report, err);
        if (origError) origError.call(obs, err);
      },
      complete: obs.complete ? (...a: unknown[]) => (obs.complete as Function).apply(obs, a) : undefined,
    };
    return args;
  }
  if (typeof first === 'function') {
    const origError = args[i + 1];
    args[i + 1] = (err: unknown) => {
      safeCall(report, err);
      if (typeof origError === 'function') (origError as Function)(err);
    };
    return args;
  }
  return args;
}

function safeCall(fn: (err: unknown) => void, err: unknown) {
  try {
    fn(err);
  } catch {
    /* telemetry never breaks the caller */
  }
}
